import Icon, { ICONS } from 'components/Icon/Icon';
import { startCase } from 'es-toolkit';
import { toUpper } from 'es-toolkit/compat';

type UtilityCardProps = {
  type: string;
  utility?: Utility;
  href?: string;
  onClick?: () => void;
  className?: string;
  'aria-label'?: string;
};

// `ICONS` is a literal with ~100 named keys and no index signature, so an
// upper-cased utility name cannot be used to index it directly. The `?.` was
// already there for names that miss.
const getIcon = (value: string) =>
  (ICONS as Record<string, { DEFAULT: string } | undefined>)[toUpper(value)]
    ?.DEFAULT;

type Utility = {
  responsibility?: string;
  utilityBillingManagedBy?: string;
  hasResidentSetup?: boolean;
  providerInstructionsId?: number;
  providerInstructions?: string;
  provider?: string;
};

/**
 * Utility card adjusted to 198 × 178 px (Figma + padding)
 *
 */
export default function UtilityCard({
  utility,
  href,
  onClick,
  className = '',
  type,
  ...rest
}: UtilityCardProps) {
  const neddsSetUp =
    !utility?.hasResidentSetup &&
    utility?.utilityBillingManagedBy === 'Provider';
  const Wrapper: any = href ? 'a' : 'button';

  return (
    <Wrapper
      href={href}
      onClick={onClick}
      type={href ? undefined : 'button'}
      className={[
        'inline-flex h-[188px] w-full relative transition-all duration-300',
        'flex-col items-center text-center justify-start overflow-hidden',
        'px-[10px] py-[20px]',
        'rounded border-[1px] ',
        !neddsSetUp
          ? ' border-navy border-solid border-[1.5px] hover:outline hover:outline-2 hover:outline-dark-navy'
          : 'border border-red border-dashed bg-red-translucent-light hover:outline hover:outline-2 hover:outline-dark-red hover:border-solid',
        'transition-shadow duration-300 hover:drop-shadow-lg',
        'cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-green-translucent',
        className,
      ].join(' ')}
      {...rest}
    >
      {/* ICON */}
      <div className="absolute -top-xs left-[50%] flex translate-x-[-50%] scale-[0.5] items-center justify-center">
        <Icon icon={getIcon(type)} />
      </div>
      {/* TEXT */}
      <div className="mt-xl shrink-0">
        <h3 className="mb-0 whitespace-nowrap text-body font-semibold text-navy">
          {type}
        </h3>
        <p className="mt-0 whitespace-nowrap text-p1 text-navy">
          {startCase(utility!.responsibility || '-')}
        </p>
      </div>
      {/* BOTTOM SECTION (anchored) */}
      <div className="mt-sm w-full">
        {!neddsSetUp ? (
          <>
            <div className="flex h-[48px] w-full items-center justify-center border-x-0 border-b-0 border-t border-dashed border-gray">
              {utility!.provider && (
                <div className="truncate text-p1 leading-p1 text-navy">
                  {utility!.provider}
                </div>
              )}
              {!utility!.provider && (
                <div className="truncate text-p1 leading-p1 text-navy">
                  Managed by {utility!.utilityBillingManagedBy || 'Homeowner'}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="mx-auto flex h-[48px] w-full items-center justify-center border-x-0 border-b-0 border-t-[1.5px] border-dashed border-red py-2xs">
            <div className="text-p1 font-semibold leading-p1 text-red">
              NEED TO SET UP
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
